import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useBusinesses } from '@/hooks/business/useBusinesses';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/core/utils';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { BaseShiftCalendar, ShiftCalendarRef } from '@/components/calendar-core';
import { useEmployees } from '@/hooks/employees';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { useTodayShiftsQuery } from '@/hooks/scheduling/useShiftsQuery';
import { calculateSingleDayDisplayHours } from '@/lib/scheduling/single-day-display-hours';
import { format } from 'date-fns';

interface TodayShiftPlanCardProps {
  className?: string;
  onClick?: () => void;
  showActionButtons?: boolean;
  onNavigateToShiftManagement?: () => void;
  showEditingHighlight?: boolean;
  areSidePanelsCollapsed?: boolean;
  businessId?: string | null;
}

// Main component - simplified without UnifiedShiftProvider
const TodayShiftPlanCard: React.FC<TodayShiftPlanCardProps> = ({
  className,
  onClick,
  showActionButtons = false,
  onNavigateToShiftManagement,
  showEditingHighlight = true,
  areSidePanelsCollapsed = false,
  businessId: propBusinessId
}) => {
  const isMobile = useIsMobile();
  const { selectedBusinessId, businesses } = useBusinesses();

  // Use prop businessId if provided, otherwise fallback to hook businessId
  const businessId = propBusinessId ?? selectedBusinessId;

  // Get employees data
  const { employees, loading: loadingEmployees } = useEmployees(businessId);

  // Use shared React Query hook for today's shifts
  const {
    shifts: todayShiftsData,
    isLoading: loadingShifts,
  } = useTodayShiftsQuery({
    businessId,
    employees,
    enabled: !!businessId,
  });

  // Get opening hours data from business details
  const selectedBusiness = businesses?.find(b => b.id === businessId);
  const openingHours = selectedBusiness?.opening_hours || [];
  const { selectedShiftDate } = useDashboardTab();
  const [forceUpdateKey, setForceUpdateKey] = useState(0);
  const shiftCalendarRef = useRef<ShiftCalendarRef>(null);

  // Use shifts directly from React Query - already properly transformed
  const todayShifts = todayShiftsData || [];

  // Calculate display hours using unified calculation (shifts + opening hours)
  const todayDateStr = format(new Date(), 'yyyy-MM-dd');
  const { displayStartTime, displayEndTime } = useMemo(() => {
    // Format shifts for the display hours calculator
    const shiftsForCalc = todayShifts.map(shift => ({
      startTime: format(shift.startTime, 'HH:mm'),
      endTime: format(shift.endTime, 'HH:mm')
    }));

    // Use unified calculation: min(shifts, opening) for start, max(shifts, opening) for end
    const displayRange = calculateSingleDayDisplayHours(
      shiftsForCalc,
      openingHours,
      todayDateStr
    );

    const now = new Date();
    const startHour = displayRange.start;
    const endHour = displayRange.end;

    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), startHour, 0, 0, 0);
    // Handle overnight hours (end > 24)
    const todayEnd = endHour >= 24
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
      : new Date(now.getFullYear(), now.getMonth(), now.getDate(), endHour, 0, 0, 0);

    return { displayStartTime: todayStart, displayEndTime: todayEnd };
  }, [todayShifts, openingHours, todayDateStr]);

  // Scroll to current time when component mounts
  useEffect(() => {
    if (shiftCalendarRef.current) {
      shiftCalendarRef.current.scrollToCurrentTime();
    }
  }, []);

  // Force re-render when selectedShiftDate changes to update visual indicators
  useEffect(() => {
    if (selectedShiftDate) {
      setForceUpdateKey(prev => prev + 1);
    }
  }, [selectedShiftDate]);

  // Only show loading state when there's no existing data
  const showLoadingState = (loadingShifts || loadingEmployees) && todayShifts.length === 0;

  return (
    <>
      {showLoadingState ? (
        <Card
          className={cn("w-full h-full", showActionButtons ? "" : "cursor-pointer", className)}
          onClick={showActionButtons ? undefined : onClick}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium">Today's Shift Plan</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {loadingShifts ? 'Loading shifts...' : 'Loading employees...'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className={cn("w-full", className)}>
            <BaseShiftCalendar
              ref={shiftCalendarRef} // Pass the ref to BaseShiftCalendar
              variant="insights"
              className={cn(
                "w-full", 
                isMobile ? "min-h-[150px]" : "min-h-[200px]", 
                showActionButtons ? "" : "cursor-pointer"
              )}
              onClick={showActionButtons ? undefined : onClick}
              currentDisplayMonth={new Date()} // Display current month
              shifts={todayShifts || []} // Use todayShifts from direct query
              workingHours={[]} // Pass empty array like other working tabs
              openingHours={openingHours} // Pass actual opening hours for business hours styling
              displayStartTime={displayStartTime} // Pass explicit start time from display hours calculation
              displayEndTime={displayEndTime} // Pass explicit end time from display hours calculation
              enableShiftAwareTimeRange={true} // Enable shift-aware time range like working tabs
              businessId={businessId}
              employees={employees} // Pass employees from hook
              displayOnlyDay={new Date()} // Display only today
              showActionButtons={showActionButtons} // Show action buttons when showActionButtons is true
              onNavigateToShiftManagement={onNavigateToShiftManagement}
              showEditingHighlight={showEditingHighlight}
              areSidePanelsCollapsed={areSidePanelsCollapsed} // Pass sidebar state for width calculations
              // Pass loading states to prevent context dependency
              loadingShifts={loadingShifts}
              loadingEmployees={loadingEmployees}
              loadingDisplayHours={false} // TodayShiftPlanCard handles display hours locally
              key={`today-calendar-${forceUpdateKey}`} // Force re-render when editing date changes
            />
        </div>
      )}
    </>
  );
};

export default TodayShiftPlanCard;
